import React from "react";
import { FileText, Sparkles } from "lucide-react";
import PageMeta from "../components/PageMeta";
import CTABanner from "../components/CTABanner";

const SECTIONS = [
  {
    title: "1. Acceptance of these terms",
    body: [
      "By accessing our website, filling in a form, subscribing to our communications or engaging Prime Wiki Services (\"we\", \"us\", \"our\") for any service, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the site or our services.",
    ],
  },
  {
    title: "2. The services we offer",
    body: [
      "Prime Wiki Services is an independent editorial agency. We assist individuals and organisations with Wikipedia page creation, neutral-point-of-view editing, notability audits, post-publication monitoring and translation into supported Wikipedia language editions.",
      "We are a fully independent private company. We are not affiliated with, endorsed by, or acting on behalf of the Wikimedia Foundation or Wikipedia itself.",
    ],
  },
  {
    title: "3. Wikipedia policy compliance",
    body: [
      "All work we deliver follows Wikipedia's editorial policies — including WP:NPOV (neutral point of view), WP:GNG / WP:BIO (general notability / biographies of living persons), WP:COI (conflict of interest), WP:V (verifiability), and WP:PAID (paid-contribution disclosure).",
      "Because Wikipedia is community-edited, publication, retention and visibility of any page we work on is ultimately decided by the Wikipedia community — not by us. We therefore do not and cannot guarantee publication, permanent visibility or protection against future edits.",
    ],
  },
  {
    title: "4. Client obligations",
    body: [
      "You agree to provide accurate, verifiable information and to not ask us to publish content you know to be untrue, defamatory, infringing, or a violation of Wikipedia policy. You also agree that any materials you share with us are either owned by you or properly licensed for our use.",
    ],
  },
  {
    title: "5. Fees, payments and discounts",
    body: [
      "Engagement fees, deliverables and timelines are set out in an individually-signed Statement of Work. Any promotional discounts (including the \"Reserve Your Discount\" offer) are valid only for the period stated at the time of your enquiry and apply to the initial engagement fee only.",
      "Invoices are payable within 14 days of issue unless otherwise agreed. Late payments may attract a 2% monthly service charge.",
    ],
  },
  {
    title: "6. Refunds",
    body: [
      "If our written notability audit concludes you do not qualify for a Wikipedia page under WP:GNG / WP:BIO, we will refund the deposit paid, minus any third-party costs already incurred on your behalf.",
      "Once drafting begins, fees for work already performed are non-refundable. We will always discuss scope and milestones transparently before invoicing for the next phase.",
    ],
  },
  {
    title: "7. Confidentiality",
    body: [
      "We treat every engagement as confidential by default. Client identities, draft content and source materials are never shared outside our editorial team and the minimum set of processors required to deliver the work. An NDA is available on request and is signed automatically for Signature and Enterprise engagements.",
    ],
  },
  {
    title: "8. Intellectual property",
    body: [
      "You retain ownership of any materials you provide to us. Upon full payment, you also own the bespoke written content we prepare for your Wikipedia page — with the caveat that once the content is published to Wikipedia, it becomes subject to the Creative Commons CC BY-SA 4.0 licence under which all Wikipedia content is published.",
    ],
  },
  {
    title: "9. Limitation of liability",
    body: [
      "To the maximum extent permitted by law, our total aggregate liability for any claim arising out of or connected with a paid engagement is limited to the fees actually paid by you to us for the engagement giving rise to the claim.",
      "We are not liable for any indirect, consequential, incidental or loss-of-profits damages.",
    ],
  },
  {
    title: "10. Termination",
    body: [
      "Either party may terminate an engagement with 14 days' written notice. Upon termination you remain liable for any fees earned up to the termination date. Sections that by their nature survive termination (confidentiality, IP, liability) will continue to apply.",
    ],
  },
  {
    title: "11. Governing law",
    body: [
      "These terms are governed by the laws of the State of New York, USA, and any dispute arising under them is subject to the exclusive jurisdiction of the state and federal courts located in the borough of Manhattan, New York.",
    ],
  },
  {
    title: "12. Updates to these terms",
    body: [
      "We may update these terms from time to time. Continued use of the site or our services after changes take effect constitutes acceptance of the updated terms.",
    ],
  },
  {
    title: "13. Contact",
    body: [
      "Any notice or request under these terms should be sent to info@Primewikiservices.com or by post to: Prime Wiki Services, 258 Vandergrift Ave, Los Angeles, CA 90049, USA.",
    ],
  },
];

export default function Terms() {
  return (
    <div className="overflow-hidden" data-testid="terms-page">
      <PageMeta
        title="Terms & Conditions — Prime Wiki Services"
        description="The terms that govern your use of the Prime Wiki Services website and engagement of our Wikipedia editorial services."
        keywords="terms and conditions, my wiki brand terms, service agreement, engagement terms"
        noindex={false}
      />

      {/* Hero banner */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8" data-testid="terms-hero">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <FileText className="h-3.5 w-3.5" /> Terms &amp; Conditions
          </span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
            A fair, plain-English agreement for working together.
          </h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">
            The rules of the road when you use our website or engage us to build, edit, translate or maintain a Wikipedia page.
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">Last updated · 24 February 2026</p>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="terms-body">
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <article key={s.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_40px_-25px_rgba(10,37,64,0.25)]">
              <h2 className="font-serif text-2xl font-semibold text-[#0A2540]">{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i} className="mt-3 text-[15px] leading-relaxed text-slate-600">{p}</p>
              ))}
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-sm text-slate-700">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" /> In plain English
          </div>
          <p className="mt-3">
            We'll do rigorous, policy-compliant work. You'll pay us on time and give us honest source material. Wikipedia's community — not us — makes the final call on publication. If any of the above changes, we'll tell you up front.
          </p>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
